import React, { useMemo, useState } from 'react';
import moment from 'moment';
import parse from 'html-react-parser';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAppDispatch } from '../../../../../../base/hooks';
import { Currency } from '../../../../../../base/interfaces/config';
import { Icon } from '../../../../../../base/components/Icon';
import { __t } from '../../../../../../base/utils/t';
import { getCurrencyById, mapRateTypeToString } from '../../../../../../base/utils';
import { HoverTooltip } from '../../../../../../base/components/HoverTooltip';
import { ActivityWidget } from '../../ActivityWidget';
import { Button } from '../../../../../../base/components/Button';
import { PopupMenu, PopupMenuOption } from '../../../../../../base/components/PopupMenu';
import { vacancyListActions, VacancyListItem } from '../../../redux/slices/vacancyListSlice';
import { CloseVacancyModal } from '../../CloseVacancyModal';

import './styles.scss';

interface Props {
    vacancyData: VacancyListItem;
    currencies: Currency[];
    upgradeVacancyHandler: (vacancyId: string) => void;
}

export const VacancyRow = ({
    vacancyData: {
        id,
        name,
        isActive,
        isDraft,
        isUpgraded,
        rateType,
        rateFrom,
        rateTo,
        currencyId,
        updatedAt,
        responses,
        recommendations,
        views,
        city,
    },
    currencies,
    upgradeVacancyHandler,
}: Props) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const [showCloseModal, setShowCloseModal] = useState(false);

    const toggleCloseModalVisibility = () => setShowCloseModal(!showCloseModal);

    const idColumn = useMemo(
        () => (
            <div className='vacancy-table__id-column'>
                <Link to={`/vacancy/view/${id}`} className='vacancy-row__link' target='_blank'>
                    {id}
                </Link>
            </div>
        ),
        [id],
    );

    const dateColumn = useMemo(() => {
        const expirationDate = moment(updatedAt).add(30, 'days');
        const isAboutToExpire = moment().startOf('day').isAfter(expirationDate.subtract(3, 'day').startOf('day'));

        return (
            <div className='vacancy-table__date-column'>
                <div className='vacancy-date'>
                    <div className='vacancy-date__date'>{moment(updatedAt).format('DD.MM.YY')}</div>

                    {isActive && (
                        <HoverTooltip
                            className='vacancy-date__tooltip'
                            text={`${__t('Вакансия автоматически закроется')} ${moment(expirationDate).format(
                                'DD.MM.YY',
                            )}`}
                            theme='dark'
                            showArrow={true}
                        >
                            <Icon
                                icon='clock-regular'
                                color={isAboutToExpire ? 'danger' : 'info'}
                                size='14'
                                className='vacancy-date__icon'
                            />
                        </HoverTooltip>
                    )}
                </div>

                {isActive && <div className='vacancy-status-label vacancy-status-label--published'>{__t('Открыта')}</div>}

                {isDraft && <div className='vacancy-status-label vacancy-status-label--unpublished'>{__t('Чернетка')}</div>}

                {!isActive && !isDraft && <div className='vacancy-status-label vacancy-status-label--archived'>{__t('Закрыта')}</div>}
            </div>
        );
    }, [isActive, isDraft, updatedAt]);

    const responsesColumn = useMemo(
        () => (
            <div className='vacancy-table__responses-column'>
                <ActivityWidget vacancyId={id} responses={responses} className='vacancy-table__activity-widget' />
            </div>
        ),
        [responses],
    );

    const vacancyColumn = useMemo(() => {
        const rateString = `${rateFrom} - ${rateTo} ${
            getCurrencyById(currencies, currencyId)?.sign
        }/${mapRateTypeToString(rateType)}`;
        return (
            <div className='vacancy-table__vacancy-column'>
                <Link to={`/vacancy/view/${id}`} className='vacancy-row__vacancy-name' title={name} target='_blank'>
                    {isUpgraded ? <span className='vacancy-row__hot-label'>&#x1f525;&nbsp;</span> : ''}
                    <span className='vacancy-row__vacancy-name-text-elipsis'>{name}</span>
                </Link>
                <div className='vacancy-row__vacancy-data'>
                    <div className='vacancy-row__vacancy-data-item'>
                        {parse(city.country.flag)}
                        <span className='vacancy-row__city' title={city.name}>
                            {city.name}
                        </span>
                    </div>
                    <div className='vacancy-row__vacancy-data-item'>
                        <Icon icon='coins-solid' color='info' size='14' className='vacancy-row__rate-icon' />
                        <span className='vacancy-row__rate-text' title={rateString}>
                            {rateString}
                        </span>
                    </div>
                </div>
            </div>
        );
    }, [id, isUpgraded, name, rateFrom, rateTo, currencies, currencyId, rateType, city]);

    const cvColumn = useMemo(
        () => (
            <div className='vacancy-table__cv-column'>
                {recommendations && recommendations.workers && (
                    <a href={recommendations.workers.url} className='vacancy-row__cv' target='_blank' rel='noreferrer'>
                        <Icon
                            icon='user-helmet-safety-solid'
                            size='14'
                            color='highlight'
                            className='vacancy-row__cv-icon'
                        />
                        {recommendations.workers.count}
                    </a>
                )}
            </div>
        ),
        [recommendations],
    );

    const viewsColumn = useMemo(
        () => (
            <div className='vacancy-table__views-column'>
                <div className='vacancy-row__views'>
                    <Icon icon='eye-solid' size='14' color='info' className='vacancy-row__views-icon' />
                    {views}
                </div>
            </div>
        ),
        [views],
    );

    const actionsColumn = useMemo(() => {
        let button;
        if (isDraft || !isActive) {
            button = (
                <Button
                    onClick={() => dispatch(vacancyListActions.openVacancyRequest(id))}
                    className='vacancy-row__action-button'
                >
                    {__t('Опубликовать')}
                </Button>
            );
        }

        if(isActive && !isUpgraded) {
            button = (
                <Button
                    type='secondary'
                    onClick={() => upgradeVacancyHandler(id)}
                    className='vacancy-row__action-button'
                >
                    {__t('Продвигать')}
                </Button>
            );
        }

        if(isActive && isUpgraded) {
            button = <span className='vacancy-row__premium-label'>{__t('Premium')}</span>;
        }

        let options: PopupMenuOption[] = [
            {
                icon: 'pencil-light',
                text: __t('Редактировать'),
                onClick: () => navigate(`/vacancy/update/${id}`, { state: { from: location } }),
            },
            {
                icon: 'copy-light',
                text: __t('Дублировать'),
                onClick: () => navigate(`/vacancy/duplicate/${id}`, { state: { from: location } }),
            },
        ];

        if (isActive) {
            options = [
                ...options,
                {
                    icon: 'arrow-rotate-right-light',
                    text: __t('Обновить вакансию'),
                    onClick: () => dispatch(vacancyListActions.renewVacancyRequest(id)),
                    disabled: !window.GLOBALS.canRenewVacancy,
                }
            ];
        }

        if(isActive || isDraft) {
            options = [
                ...options,
                {
                    icon: 'ban-light',
                    text: __t('Закрыть вакансию'),
                    onClick: toggleCloseModalVisibility,
                },
            ];
        }

        return (
            <div className='vacancy-table__actions-column vacancy-row__actions-column'>
                {button}
                <PopupMenu options={options} align='right'>
                    <Icon icon='ellipsis-regular' size='20' color='grey' />
                </PopupMenu>
            </div>
        );
    }, [id, isActive, isDraft, isUpgraded]);

    const closedOverlay = useMemo(() => {
        if (!isActive && !isDraft) {
            return <div className='vacancy-row__closed-overlay'></div>;
        }
        return null;
    }, [isActive, isDraft]);

    return (
        <>
            <div className='vacancy-row'>
                {closedOverlay}
                {idColumn}
                {dateColumn}
                {vacancyColumn}
                {responsesColumn}
                {cvColumn}
                {viewsColumn}
                {actionsColumn}
            </div>
            <CloseVacancyModal
                vacancyId={id}
                visible={showCloseModal}
                actionAfterSubmit='reload'
                visibilityHandler={toggleCloseModalVisibility}
            />
        </>
    );
};
